import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Loader2, MapPin, Phone, Mail, Globe, Facebook, Instagram, Linkedin, Music2, Youtube,
  BadgeCheck, Building2, MessageCircle, ArrowRight, Sparkles, Award, Users,
} from 'lucide-react';
import api from '../../lib/api';
import { LOGO_DARK_BG } from '../../lib/constants';
import ShareMenu from '../../components/ShareMenu';
import { OPERATION_LABELS } from '../../lib/constants';

const HERO_BG =
  'https://customer-assets-v7afamib.emergentagent.net/job_cni-inmobiliaria/artifacts/0efefdebd3049002_Panor%C3%A1mica%20urbana%20al%20atardecer.png';

export default function MicrositioSocio({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api
      .get(`/directory/${slug}`)
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cnib-navy">
        <Loader2 className="h-10 w-10 animate-spin text-orange-500" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-cnib-navy p-6 text-center">
        <h1 className="font-heading text-3xl font-bold text-white">Micrositio no encontrado</h1>
        <p className="text-slate-300">El perfil que buscas no está disponible o fue desactivado.</p>
        <Link to="/" className="rounded-full bg-orange-500 px-6 py-3 font-bold text-white">
          Ir a CNIB.bo
        </Link>
      </div>
    );
  }

  const p = data.profile;
  const whatsappDigits = (p.whatsapp || '').replace(/\D/g, '');
  const whatsappUrl = whatsappDigits
    ? `https://wa.me/${whatsappDigits}?text=${encodeURIComponent(
        `Hola ${p.name}, llego desde su micrositio profesional en CNIB.`
      )}`
    : null;
  const socials = [
    p.social_facebook && { icon: Facebook, href: p.social_facebook, label: 'Facebook', color: 'bg-[#1877F2]' },
    p.social_instagram && { icon: Instagram, href: p.social_instagram, label: 'Instagram', color: 'bg-gradient-to-tr from-pink-500 via-rose-500 to-amber-400' },
    p.social_linkedin && { icon: Linkedin, href: p.social_linkedin, label: 'LinkedIn', color: 'bg-[#0A66C2]' },
    p.social_tiktok && { icon: Music2, href: p.social_tiktok, label: 'TikTok', color: 'bg-gradient-to-br from-slate-900 to-rose-500' },
    p.social_youtube && { icon: Youtube, href: p.social_youtube, label: 'YouTube', color: 'bg-[#FF0000]' },
    p.social_website && { icon: Globe, href: p.social_website, label: 'Sitio web', color: 'bg-slate-700' },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="micrositio-socio">
      {/* HERO */}
      <section className="hero-grain relative overflow-hidden bg-cnib-navy pb-28 pt-6 md:pb-40 md:pt-10">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-cnib-navy/95 via-cnib-navy/70 to-cnib-navy/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-cnib-navy via-transparent to-cnib-navy/40" />
        </div>

        {/* Top bar */}
        <div className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_DARK_BG} alt="CNIB" className="h-10 w-auto" />
            <span className="hidden text-[10px] font-bold uppercase tracking-wider text-orange-400 sm:block">
              Micrositio profesional
            </span>
          </Link>
          <ShareMenu
            title={`${p.name} — Corredor CNIB`}
            url={typeof window !== 'undefined' ? window.location.href : ''}
            compact
            label="Compartir"
            testid="micrositio-share"
          />
        </div>

        <div className="relative z-10 mx-auto grid max-w-6xl items-center gap-10 px-4 pt-10 sm:px-6 md:pt-16 lg:grid-cols-5 lg:px-8">
          {/* Left column */}
          <div className="lg:col-span-3">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-orange-500/50 bg-orange-500/15 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-orange-300"
            >
              <Sparkles className="h-3 w-3" /> Socio certificado CNIB
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-4 font-heading text-4xl font-extrabold leading-[1.05] text-white sm:text-5xl lg:text-6xl"
              data-testid="micrositio-name"
            >
              {p.name}
              {p.years_experience > 0 && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="ml-3 inline-block align-middle text-base font-semibold text-orange-400"
                >
                  · {p.years_experience} años en el mercado
                </motion.span>
              )}
            </motion.h1>

            {p.company && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mt-3 text-lg font-semibold text-orange-400"
              >
                {p.company}
              </motion.p>
            )}

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mt-5 flex items-center gap-2 text-sm text-slate-300"
            >
              <MapPin className="h-4 w-4 text-orange-500" />
              {p.city || 'Bolivia'}
              {p.coverage_zones?.length > 0 && (
                <span className="text-slate-400">· {p.coverage_zones.slice(0, 3).join(' · ')}</span>
              )}
            </motion.p>

            {Array.isArray(p.specialties) && p.specialties.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-5 flex flex-wrap gap-2"
              >
                {p.specialties.map((s) => (
                  <span key={s} className="rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white backdrop-blur">
                    {s}
                  </span>
                ))}
              </motion.div>
            )}

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              {whatsappUrl && (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="micrositio-whatsapp"
                  className="group inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-bold text-white shadow-xl shadow-emerald-500/30 transition-all hover:-translate-y-0.5 hover:bg-emerald-600"
                >
                  <MessageCircle className="h-4 w-4" />
                  Escribir por WhatsApp
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
              )}
              {p.email && (
                <a
                  href={`mailto:${p.email}`}
                  data-testid="micrositio-email"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-6 py-3 text-sm font-bold text-white backdrop-blur transition-all hover:-translate-y-0.5 hover:bg-white/20"
                >
                  <Mail className="h-4 w-4" />
                  Enviar correo
                </a>
              )}
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="mt-10 grid max-w-md grid-cols-3 gap-3"
            >
              {[
                { icon: Building2, value: data.properties?.length || 0, label: 'Propiedades activas' },
                { icon: Award, value: p.years_experience || 1, label: 'Años de experiencia' },
                { icon: Users, value: p.specialties?.length || 1, label: 'Especialidades' },
              ].map((s, i) => (
                <div key={i} className="rounded-2xl border border-white/15 bg-white/10 p-3 text-center backdrop-blur">
                  <s.icon className="mx-auto h-4 w-4 text-orange-400" />
                  <p className="mt-1 font-heading text-xl font-extrabold text-white">+{s.value}</p>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-300">{s.label}</p>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right column: avatar + socials */}
          <div className="relative lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="relative mx-auto w-full max-w-sm"
            >
              <div className="absolute -inset-6 rounded-[32px] bg-gradient-to-br from-orange-500/50 via-cnib-blue/40 to-transparent blur-2xl" />
              <div className="relative overflow-hidden rounded-[32px] border border-white/10 bg-cnib-navy/60 p-6 backdrop-blur">
                {p.picture ? (
                  <img
                    src={p.picture}
                    alt={p.name}
                    className="mx-auto h-56 w-56 rounded-3xl object-cover shadow-2xl ring-4 ring-white/20"
                    data-testid="micrositio-avatar"
                  />
                ) : (
                  <span className="mx-auto flex h-56 w-56 items-center justify-center rounded-3xl bg-gradient-to-br from-orange-500 to-cnib-blue text-7xl font-black text-white shadow-2xl ring-4 ring-white/20">
                    {p.name?.charAt(0) || 'S'}
                  </span>
                )}

                <div className="mt-4 flex items-center justify-center gap-1.5 text-xs font-bold text-emerald-400">
                  <BadgeCheck className="h-4 w-4" />
                  Verificado por CNIB Bolivia
                </div>

                {p.member_number && (
                  <p className="mt-1 text-center text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    N° {p.member_number}
                  </p>
                )}

                {socials.length > 0 && (
                  <div className="mt-5 flex justify-center gap-2">
                    {socials.map((s) => (
                      <a
                        key={s.label}
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex h-10 w-10 items-center justify-center rounded-full ${s.color} text-white shadow-lg transition-transform hover:-translate-y-0.5`}
                        aria-label={s.label}
                      >
                        <s.icon className="h-4 w-4" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BIO + CONTACT */}
      {(p.bio || p.coverage_zones?.length > 0 || p.languages?.length > 0 || p.whatsapp) && (
        <section className="mx-auto -mt-20 max-w-6xl px-4 sm:px-6 lg:px-8" data-testid="micrositio-about">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
            className="grid gap-6 rounded-3xl border border-slate-100 bg-white p-6 shadow-2xl md:p-10 lg:grid-cols-3"
          >
            {p.bio && (
              <div className="lg:col-span-2">
                <p className="text-xs font-bold uppercase tracking-wider text-orange-500">Sobre mí</p>
                <h2 className="mt-1 font-heading text-2xl font-extrabold text-slate-900">
                  Un corredor profesional a tu servicio
                </h2>
                <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-slate-600">{p.bio}</p>
              </div>
            )}
            <aside className="space-y-5">
              {p.coverage_zones?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Zonas que cubro</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {p.coverage_zones.map((z) => (
                      <span key={z} className="rounded-full bg-cnib-blue/5 px-2.5 py-0.5 text-xs font-bold text-cnib-blue">
                        {z}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {p.languages?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Idiomas</p>
                  <p className="mt-1 text-sm text-slate-700">{p.languages.join(' · ')}</p>
                </div>
              )}
              {p.whatsapp && (
                <div className="space-y-1.5">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Contacto directo</p>
                  <p className="flex items-center gap-2 text-sm text-slate-700">
                    <Phone className="h-4 w-4 text-cnib-blue" /> {p.whatsapp}
                  </p>
                  {p.email && (
                    <p className="flex items-center gap-2 text-sm text-slate-700">
                      <Mail className="h-4 w-4 text-cnib-blue" /> {p.email}
                    </p>
                  )}
                </div>
              )}
            </aside>
          </motion.div>
        </section>
      )}

      {/* PROPERTIES */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8" data-testid="micrositio-properties">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-wrap items-end justify-between gap-3"
        >
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-orange-500">Portafolio</p>
            <h2 className="mt-1 font-heading text-3xl font-extrabold text-slate-900 sm:text-4xl">
              Propiedades disponibles
            </h2>
          </div>
          <span className="rounded-full bg-slate-100 px-4 py-1.5 text-xs font-bold text-slate-600">
            {data.properties?.length || 0} activas
          </span>
        </motion.div>

        {!data.properties || data.properties.length === 0 ? (
          <div
            className="mt-6 rounded-3xl border border-dashed border-slate-300 bg-white py-16 text-center text-sm text-slate-500"
            data-testid="micrositio-no-properties"
          >
            Este socio todavía no publica propiedades en el MLS.
          </div>
        ) : (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="micrositio-properties-grid">
            {data.properties.map((pr, idx) => (
              <motion.a
                key={pr.property_id}
                href={`/propiedades/${pr.property_id}`}
                data-testid={`micrositio-property-${pr.property_id}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: idx * 0.06 }}
                whileHover={{ y: -4 }}
                className="group overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-lg transition-shadow hover:shadow-2xl"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                  {pr.images?.[0] ? (
                    <img
                      src={pr.images[0]}
                      alt=""
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  ) : (
                    <Building2 className="absolute inset-0 m-auto h-10 w-10 text-slate-300" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
                  <span className="absolute left-3 top-3 rounded-full bg-orange-500 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                    {OPERATION_LABELS[pr.operation] || pr.operation}
                  </span>
                  <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between text-white">
                    <div className="min-w-0">
                      <p className="truncate text-xs font-semibold">{pr.zone ? `${pr.zone}, ` : ''}{pr.city}</p>
                    </div>
                    <p className="shrink-0 font-heading text-lg font-extrabold">
                      US$ {Number(pr.price_usd).toLocaleString('es-BO')}
                    </p>
                  </div>
                </div>
                <div className="p-5">
                  <p className="line-clamp-2 font-heading text-base font-extrabold text-slate-900">{pr.title}</p>
                  <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                    <span>
                      {pr.bedrooms > 0 && `${pr.bedrooms} dorm · `}
                      {pr.bathrooms > 0 && `${pr.bathrooms} baños · `}
                      {pr.area_m2 > 0 && `${pr.area_m2} m²`}
                    </span>
                    <span className="flex items-center gap-1 font-bold text-orange-500 opacity-0 transition-opacity group-hover:opacity-100">
                      Ver <ArrowRight className="h-3 w-3" />
                    </span>
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        )}
      </section>

      {/* FOOTER CTA */}
      <section className="bg-cnib-navy py-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-4xl px-4 text-center sm:px-6"
        >
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-orange-500">¿Buscas asesoría personalizada?</p>
          <h2 className="mt-3 font-heading text-3xl font-extrabold text-white sm:text-4xl">
            Contacta a {p.name?.split(' ')[0]} y da el siguiente paso
          </h2>
          <p className="mt-3 text-slate-300">
            Micrositio profesional avalado por la Cámara Nacional Inmobiliaria de Bolivia.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {whatsappUrl && (
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-bold text-white shadow-xl transition-all hover:-translate-y-0.5"
              >
                <MessageCircle className="h-4 w-4" /> Escribir ahora
              </a>
            )}
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-3 text-sm font-bold text-white backdrop-blur transition-all hover:bg-white/15"
            >
              Conocer CNIB.bo
            </Link>
          </div>
        </motion.div>
      </section>

      <footer className="bg-cnib-navy pb-8 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} CNIB Connect · Micrositio profesional
      </footer>
    </div>
  );
}
